import { isNodeSelection } from '@tiptap/core';
import { Selection } from '@tiptap/extensions';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

/**
 * Extended Selection extension that properly handles atomic nodes.
 *
 * The default Tiptap Selection extension only shows decorations when the editor
 * is unfocused, relying on the browser's native ::selection for the focused state.
 * However, atomic inline nodes (like inline math) don't participate in native
 * text selection, so they appear unselected even when within a selection range.
 *
 * This extension adds an additional plugin that applies selection decorations
 * to atomic nodes even when the editor is focused, ensuring consistent visual
 * feedback for all selected content.
 */
export const ExtendedSelection = Selection.extend({
  addProseMirrorPlugins() {
    const parentPlugins = this.parent?.() ?? [];
    const { editor, options } = this;

    // Plugin that handles selection styling for atomic nodes when editor is focused
    const atomicNodeSelectionPlugin = new Plugin({
      key: new PluginKey('atomicNodeSelection'),
      props: {
        decorations(state) {
          const { selection, doc } = state;

          // Only apply when:
          // - Selection is not empty
          // - Editor is focused (unfocused state is handled by parent Selection)
          // - Not a node selection (those already have proper styling)
          // - Not dragging
          if (selection.empty || !editor.isFocused || isNodeSelection(selection) || editor.view.dragging) {
            return null;
          }

          const decorations: Decoration[] = [];

          // Find all atomic nodes within the selection range
          doc.nodesBetween(selection.from, selection.to, (node, pos) => {
            // Check if this is an atomic node (doesn't allow direct text editing)
            if (node.isAtom && node.isInline) {
              const nodeEnd = pos + node.nodeSize;

              // Only decorate if the node is fully within the selection
              if (pos >= selection.from && nodeEnd <= selection.to) {
                decorations.push(
                  Decoration.node(pos, nodeEnd, {
                    class: options.className,
                  }),
                );
              }
            }
          });

          if (decorations.length === 0) {
            return null;
          }

          return DecorationSet.create(doc, decorations);
        },
      },
    });

    return [...parentPlugins, atomicNodeSelectionPlugin];
  },
});
